import * as React from 'react';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faFacebookF, faInstagram, faLinkedinIn, faXTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { kebabCase } from 'lodash';
import styled, { css } from 'styled-components';
import { prop } from 'styled-tools';

import { FooterData } from 'components/Layout/Footer/FooterData';
import FooterSeparator from 'components/Layout/Footer/components/FooterSeparator';
import Link from 'components/Link';

import { dataLayerPush } from 'utils/functions';
import { getModularBlockLink, getModularBlockLinkData } from 'utils/modularBlockUtils';

const MenuHeaderLink = styled(Link)`
  color: ${(props) => props.theme.colors.white};
  text-decoration: none;

  svg {
    margin-left: 0.5rem;
    transition: color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  }

  &:hover {
    svg {
      color: ${(props) => props.theme.colors.primaryRed};
    }
  }
`;

const MenuLinkCommon = css`
  color: ${(props) => props.theme.colors.white};
  text-decoration: none;
  border-bottom: 0.06rem solid rgba(0, 0, 0, 0);
  padding-bottom: 0.25rem;
  transition: border-color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);

  &:hover {
    border-bottom-color: ${(props) => props.theme.colors.primaryRed};
    color: ${(props) => props.theme.colors.primaryRed};
    text-decoration: none;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    font-size: 1rem;
  }
`;

const MenuLink = styled(Link)`
  ${MenuLinkCommon}
`;

const MenuAnchor = styled.a`
  ${MenuLinkCommon}
`;

const SocialLinkContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.5em;
  margin-left: -0.5rem;
  margin-right: 1.805rem;

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    margin: 0 auto;
  }
`;

const SocialLink = styled(Link)`
  color: ${(props) => props.theme.colors.white};
  cursor: pointer;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);

  &:hover {
    background-color: ${(props) => props.theme.colors.primaryRed};
  }
`;

const FooterHeader = styled.h2`
  margin-bottom: 0.95rem;
  color: ${(props) => props.theme.colors.white};
  font-size: 1rem;
  line-height: 1.5em;
  letter-spacing: 0.03em;
  font-family: ${prop('theme.fonts.default')};
  font-weight: 700;
  letter-spacing: normal;
`;

const FooterLinkList = styled.div`
  grid-column-gap: 1rem;
  grid-row-gap: 1rem;
  flex-direction: column;
  align-items: flex-start;
  font-size: 0.875rem;
  font-weight: 400;
  display: flex;

  padding: 0;
  margin: 0;
  list-style-type: none;
  font-family: ${prop('theme.fonts.default')};
  color: ${(props) => props.theme.colors.white};
  font-size: 0.875rem;

  li {
    padding: 0;
    margin-bottom: 1rem;
  }
`;

const MenusContainer = styled.div`
  display: grid;
  grid-auto-columns: 1fr;
  grid-template-columns: 1fr 1fr 0.5fr 0.5fr;
  grid-template-rows: auto;
  grid-column-gap: 2rem;
  grid-row-gap: 2rem;
  grid-column: span 1 / span 1;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    grid-column-gap: 1rem;
    grid-row-gap: 1rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-row-gap: 2rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    grid-template-columns: 1fr;
  }
`;

const Menu = styled.div`
  grid-area: span 1 / span 1 / span 1 / span 1;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    grid-column: span 2 / span 2;
  }
`;

const PhoneNumberContainer = styled.div`
  display: flex;
  gap: 0.5em;
  flex-direction: row;
  align-items: flex-start;

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    flex-direction: column;
  }
`;

const LinkContainer = styled.div`
  padding-bottom: 0.35em;
`;

interface MenuProps {
  data: FooterData;
}

const MenuSection: React.FC<MenuProps> = ({ data }) => {
  const formatPhoneNumber = (phone: string) => `(${phone.slice(4, 7)}) ${phone.slice(7, 10)}-${phone.slice(10)}`;

  const socialIcons: { [site: string]: IconDefinition } = {
    twitter: faXTwitter,
    x: faXTwitter,
    linkedin: faLinkedinIn,
    facebook: faFacebookF,
    instagram: faInstagram,
    youtube: faYoutube,
  };

  const handleSocialClick = (socialName: string) => {
    dataLayerPush('social_click', 'event', {
      event_category: 'social click',
      event_action: 'click',
      social_network: socialName,
    });
  };

  return (
    <>
      <FooterSeparator data-testId={'footer-separator-1'} />
      <MenusContainer>
        {data.menus.map((menu, colIndex) => {
          const menuHeader = menu?.menuHeader;
          const menuLink = getModularBlockLink(menu.headerLink);
          const menuItems = menu?.footerMenu?.menuItems;

          return (
            <Menu key={colIndex}>
              {menuHeader &&
                (menuLink ? (
                  <MenuHeaderLink to={menuLink} data-testId={`footer-link-${kebabCase(menuHeader)}`}>
                    <FooterHeader>
                      {menuHeader}
                      <FontAwesomeIcon icon={faChevronRight} />
                    </FooterHeader>
                  </MenuHeaderLink>
                ) : (
                  <FooterHeader data-testId={`footer-link-${kebabCase(menuHeader)}`}>{menuHeader}</FooterHeader>
                ))}
              {menuItems?.some((item) => socialIcons.hasOwnProperty(item.linkText.toLowerCase())) ? (
                <SocialLinkContainer>
                  {menuItems.map((item, itemIndex) => {
                    const { link, target } = getModularBlockLinkData(item.link);
                    return (
                      <SocialLink
                        data-testId={`footer-link-${kebabCase(item.linkText)}`}
                        key={itemIndex}
                        to={link}
                        target={target}
                        onClick={() => handleSocialClick(item.linkText)}
                        aria-label={`Share on ${item.linkText}`}
                      >
                        {socialIcons.hasOwnProperty(item.linkText.toLowerCase()) ? (
                          <FontAwesomeIcon aria-hidden='true' icon={socialIcons[item.linkText.toLowerCase()]} />
                        ) : (
                          item.linkText
                        )}
                      </SocialLink>
                    );
                  })}
                </SocialLinkContainer>
              ) : (
                <FooterLinkList>
                  {menuItems?.map((item, itemIndex) => {
                    const url = getModularBlockLink(item.link);

                    return (
                      <div key={itemIndex}>
                        {url && url.startsWith('tel:') ? (
                          <PhoneNumberContainer data-testId={`footer-phonenumber-${itemIndex}`}>
                            <strong>{item.linkText}</strong>
                            <MenuAnchor href={url}>{formatPhoneNumber(url)}</MenuAnchor>
                          </PhoneNumberContainer>
                        ) : (
                          <LinkContainer>
                            {url ? (
                              <MenuLink
                                to={url}
                                data-testId={`footer-link-${kebabCase(
                                  item.linkText.includes('@')
                                    ? item.linkText.substring(0, item.linkText.indexOf('@'))
                                    : item.linkText
                                )}`}
                              >
                                {item.linkText}
                              </MenuLink>
                            ) : (
                              item.linkText
                            )}
                          </LinkContainer>
                        )}
                      </div>
                    );
                  })}
                </FooterLinkList>
              )}
            </Menu>
          );
        })}
      </MenusContainer>
    </>
  );
};

export default MenuSection;
